<template>
  <div class="p-4" v-if="amReady">
    <div class="card">
      <div class="card-header">
        <h4>Assign Modules to Roles</h4>
      </div>
      <div class="card-body">
        <el-row>
          <el-col :span="12">
            <div class="mb-3">
              <label class="form-label">Select Role</label>
              <el-select v-model="selectedRoleId" placeholder="Select a role" @change="onRoleChange" class="w-100">
                <el-option
                  v-for="role in rolesList"
                  :key="role.id"
                  :label="role.name"
                  :value="role.id">
                  <span style="float: left">{{ role.name }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">{{ role.description }}</span>
                </el-option>
              </el-select>
            </div>
          </el-col>
        </el-row>

        <div v-if="selectedRoleId" class="mt-4">
          <h5>Available Modules ({{ modulesList.length }})</h5>
          
          <!-- Debug info -->
          <div class="alert alert-info" v-if="modulesList.length === 0">
            <strong>Debug:</strong> No modules loaded. Check console for errors.
          </div>
          
          <div class="row">
            <div class="col-12">
              <el-checkbox-group v-model="selectedModules" @change="onModuleChange">
                <div class="row">
                  <div v-for="module in modulesList" :key="module.id" class="col-md-6 mb-3">
                    <el-checkbox :label="module.name" class="w-100">
                      <div class="module-item">
                        <strong>{{ module.name }}</strong>
                        <small class="text-muted d-block">{{ module.description }}</small>
                        <span class="badge badge-info">{{ module.category }}</span>
                      </div>
                    </el-checkbox>
                  </div>
                </div>
              </el-checkbox-group>
            </div>
          </div>

          <div class="mt-4 text-center">
            <el-button @click="saveModules" type="primary" :loading="saving">
              Save Modules
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import {notify, notifySuccess} from '@/system-services/notify.js'
import roleModule from './../../system-role/index.js'
import module from './../index.js'

export default {
  data() {
    return {
      amReady: false,
      selectedRoleId: null,
      selectedModules: [],
      currentRoleModules: [],
      rolesList: [],
      modulesList: [],
      saving: false
    }
  },
  methods: {
    ...mapActions(roleModule.info.name + '-api', {
      getRolesList: 'dropDownList'
    }),
    ...mapActions(module.info.name + '-api', {
      assignModules: 'assignToProfile',
      removeModules: 'removeFromProfile',
      getByProfile: 'getByProfile',
      getAvailableModules: 'getAvailableModules'
    }),

    async loadRoles() {
      try {
        const rolesData = await this.getRolesList()
        
        // Handle the response format from backend
        // Backend returns: [{value: "9", label: "Account Manager"}, ...]
        if (Array.isArray(rolesData)) {
          this.rolesList = rolesData.map(role => ({
            id: role.value, // Use 'value' as id
            name: role.label, // Use 'label' as name  
            description: role.label // Use label as description too
          }))
        } else {
          this.rolesList = rolesData || []
        }
        
        console.log('Loaded roles:', this.rolesList)
      } catch (e) {
        console.error('Error loading roles:', e)
        notify({
          type: 'error',
          title: 'Error loading roles'
        })
      }
    },

    async loadModules() {
      try {
        console.log('Starting to load modules...')
        const modulesData = await this.getAvailableModules()
        
        console.log('Raw modules response:', modulesData)
        
        // Handle the new response format from ProfileModule API
        // Backend returns: {success: true, modules: [...], count: 52}
        // Store action already extracts the modules array
        
        if (!Array.isArray(modulesData)) {
          console.error('Modules data is not an array:', modulesData)
          this.modulesList = []
          return
        }
        
        this.modulesList = modulesData.map(module => ({
          id: module.name, // Use name as ID since backend doesn't provide separate ID
          name: module.name,
          category: module.group_name || 'General',
          description: module.display_name || (module.name ? module.name.replace(/[.-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''),
          order: module.order || 1
        }))
        
        console.log('Processed modulesList:', this.modulesList)
        console.log('Module names for checkboxes:', this.modulesList.map(m => m.name))
        console.log('modulesList length:', this.modulesList.length)
      } catch (e) {
        console.error('Error loading modules:', e)
        notify({
          type: 'error',
          title: 'Error loading modules'
        })
      }
    },

    async onRoleChange() {
      if (this.selectedRoleId) {
        try {
          // Use the new ProfileModule API to get role modules
          const roleModules = await this.getByProfile({profile_id: this.selectedRoleId})
          
          // Handle new response format: modules are returned as simple string array
          // Backend returns: ["form_sign_up_fas_free_routes", "voip-customer", ...]
          this.currentRoleModules = Array.isArray(roleModules) ? roleModules : []
          
          this.selectedModules = [...this.currentRoleModules]
          
          console.log('Loaded role modules:', this.currentRoleModules)
          console.log('Available modules:', this.modulesList.map(m => m.name))
          console.log('Matching modules:', this.currentRoleModules.filter(rm => 
            this.modulesList.some(m => m.name === rm)
          ))
        } catch (e) {
          console.error('Error loading role modules:', e)
          notify({
            type: 'error',
            title: 'Error loading role modules'
          })
          // Reset selections on error
          this.currentRoleModules = []
          this.selectedModules = []
        }
      } else {
        // Reset when no role selected
        this.currentRoleModules = []
        this.selectedModules = []
      }
    },

    onModuleChange() {
      // This will be called whenever checkboxes change
    },

    async saveModules() {
      if (!this.selectedRoleId) {
        notify({
          type: 'error',
          title: 'Please select a role'
        })
        return
      }

      this.saving = true

      try {
        // Find modules to add (selected but not in current)
        const toAdd = this.selectedModules.filter(m => !this.currentRoleModules.includes(m))
        
        // Find modules to remove (in current but not selected)
        const toRemove = this.currentRoleModules.filter(m => !this.selectedModules.includes(m))

        // Add new modules
        if (toAdd.length > 0) {
          try {
            await this.assignModules({
              profile_id: this.selectedRoleId,
              modules: toAdd
            })
            console.log(`Successfully assigned modules:`, toAdd)
          } catch (error) {
            console.error(`Failed to assign modules:`, error)
            notify({
              type: 'error',
              title: `Failed to assign modules: ${toAdd.join(', ')}`
            })
          }
        }

        // Remove modules
        if (toRemove.length > 0) {
          try {
            await this.removeModules({
              profile_id: this.selectedRoleId,
              modules: toRemove
            })
            console.log(`Successfully removed modules:`, toRemove)
          } catch (error) {
            console.error(`Failed to remove modules:`, error)
            notify({
              type: 'error',
              title: `Failed to remove modules: ${toRemove.join(', ')}`
            })
          }
        }

        this.currentRoleModules = [...this.selectedModules]
        notifySuccess('Modules updated successfully')
      } catch (e) {
        notify({
          type: 'error',
          title: 'Error updating modules'
        })
      }

      this.saving = false
    }
  },

  async created() {
    await this.loadRoles()
    await this.loadModules()
    this.amReady = true
  }
}
</script>

<style scoped>
.module-item {
  text-align: left;
}

.module-item strong {
  display: block;
  margin-bottom: 4px;
}

.module-item small {
  font-size: 11px;
  line-height: 1.3;
}

.module-item .badge {
  margin-top: 4px;
  font-size: 10px;
}
</style>